<template>
  <div class="contract-change-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('contractChangeManage.list.title')" :subtitle="$t('contractChangeManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.contractName" clearable
            :placeholder="$t('contractChangeManage.search.contractName')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.contractCode" clearable
            :placeholder="$t('contractChangeManage.search.contractCode')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="searchForm.contractType" clearable class="w-full"
            :placeholder="$t('contractChangeManage.search.contractType')">
            <el-option v-for="item in contractTypes" :key="item.contractTypeId" :label="item.typeName"
              :value="item.contractTypeId" />
          </el-select>
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('contractChangeManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleToChangePage('contractChangeMainBody')">
            {{ $t('contractChangeManage.list.mainBodyChange') }}
          </el-button>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleToChangePage('contractChangeLease')">
            {{ $t('contractChangeManage.list.leaseAdjustment') }}
          </el-button>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleToChangePage('contractChangeAssets')">
            {{ $t('contractChangeManage.list.assetsChange') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('contractChangeManage.page.empty')" @retry="getList">
        <el-table-column prop="contractName" :label="$t('contractChangeManage.table.contractName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="contractCode" :label="$t('contractChangeManage.table.contractCode')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.contractCode || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="contractTypeName" :label="$t('contractChangeManage.table.contractType')" align="center" width="120" />
        <el-table-column prop="partyA" :label="$t('contractChangeManage.table.partyA')" align="center" min-width="130" show-overflow-tooltip />
        <el-table-column prop="partyB" :label="$t('contractChangeManage.table.partyB')" align="center" min-width="130" show-overflow-tooltip />
        <el-table-column prop="planTypeName" :label="$t('contractChangeManage.table.changeType')" align="center" width="120" />
        <el-table-column prop="changePersonName" :label="$t('contractChangeManage.table.changePerson')" align="center" width="110" />
        <el-table-column prop="createTime" :label="$t('contractChangeManage.table.applyTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('contractChangeManage.table.remark')" align="left" min-width="150" show-overflow-tooltip />
        <el-table-column prop="stateName" :label="$t('contractChangeManage.table.state')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName" :text="scope.row.stateName" type="info" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button v-if="scope.row.state === '11'" type="text" size="mini" @click="handleOpenDeleteDialog(scope.row)">
              {{ $t('common.cancel') }}
            </el-button>
            <el-button type="text" size="mini" @click="handleToDetails(scope.row)">
              {{ $t('common.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <div class="ds-note-text">
        {{ $t('contractChangeManage.note') }}
      </div>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 删除确认对话框 -->
    <delete-contract-change-plan ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import { queryContractChangePlan, queryContractType } from '@/api/contract/contractChangeManageApi'
import DeleteContractChangePlan from '@/components/contract/deleteContractChangePlan'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ContractChangeManageList',
  components: {
    DeleteContractChangePlan,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        contractName: '',
        contractCode: '',
        contractType: ''
      },
      tableData: [],
      contractTypes: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
    this.getContractTypes()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, total } = await queryContractChangePlan(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取合同变更列表失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('contractChangeManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    async getContractTypes() {
      try {
        const params = {
          page: 1,
          row: 50
        }
        const { data } = await queryContractType(params)
        this.contractTypes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('获取合同类型失败:', error)
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        contractName: '',
        contractCode: '',
        contractType: ''
      }
      this.handleSearch()
    },
    handleToChangePage(type) {
      this.$router.push(`/views/contract/contractChangeDetail?param=${type}`)
    },
    handleToDetails(row) {
      this.$router.push(`/views/contract/contractChangeDetails?planId=${row.planId}`)
    },
    handleOpenDeleteDialog(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-change-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-note-text {
  padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
  color: var(--ds-gray-500);
  font-size: var(--ds-font-size-sm);
  border-top: 1px dashed var(--ds-gray-200);
}
</style>
